import { CASE_TYPE_SCHEMA, emptyValueForSchema, getSchemaAt } from "../../../../case-spec";
import {
  ArrayShell,
  MediaUrlField,
  RowCard,
  TextField,
  useOpenIndex,
} from "../shared-ui";
import { asArray, isRecord, num, str, type PanelProps } from "../shared";
import { appendAtPath, childPath, getAtPath, setAtPath } from "../path-utils";

const FILES_PATH = "device.apps.files";

function sizeLabel(bytes: number): string {
  if (bytes <= 0) return "未知";
  if (bytes < 1000) return `${bytes} B`;
  if (bytes < 1000 * 1000) return `${Math.round(bytes / 1000)} KB`;
  return `${(bytes / 1000 / 1000).toFixed(1)} MB`;
}

/** Files app documents generated by AI or uploaded by ops. */
export function FilesPanel({ root, disabled, onChangeRoot }: PanelProps) {
  const items = asArray(getAtPath(root, FILES_PATH));
  const { openIndex, setOpenIndex, toggle } = useOpenIndex(items.length);
  const itemSchema = getSchemaAt(CASE_TYPE_SCHEMA, `${FILES_PATH}[0]`);

  return (
    <ArrayShell
      title="文件"
      count={items.length}
      disabled={disabled}
      emptyText="暂无 PDF，点击「添加」上传。手机「文件」App 还会自动列出相册里最新的 12 张图片。"
      onAdd={() => {
        const row = itemSchema
          ? emptyValueForSchema(itemSchema)
          : { filename: "", added_at: "", file_url: "" };
        onChangeRoot(appendAtPath(root, FILES_PATH, row));
        setOpenIndex(items.length);
      }}
    >
      <p className="text-xs text-muted-foreground">
        上传 PDF 后会显示在手机「文件」App 的最近项目里，排在图片前面。
      </p>
      <ul className="flex flex-col gap-2">
        {items.map((item, index) => {
          const rec = isRecord(item) ? item : {};
          const itemPath = childPath(FILES_PATH, index);
          const bytes = num(rec.size_bytes);
          return (
            <RowCard
              key={itemPath}
              title={str(rec.filename) || `文件 ${index + 1}`}
              open={openIndex === index}
              onToggle={() => toggle(index)}
              disabled={disabled}
              index={index}
              total={items.length}
              arrayPath={FILES_PATH}
              root={root}
              onChangeRoot={onChangeRoot}
            >
              <MediaUrlField
                label="PDF 文件"
                value={str(rec.file_url)}
                disabled={disabled}
                mediaKind="pdf"
                fieldKey="file_url"
                placeholder="https://…"
                onChange={(v) =>
                  onChangeRoot(setAtPath(root, childPath(itemPath, "file_url"), v))
                }
                onUpload={(uploaded) => {
                  let next = setAtPath(root, childPath(itemPath, "file_url"), uploaded.url);
                  next = setAtPath(next, childPath(itemPath, "filename"), uploaded.name);
                  next = setAtPath(next, childPath(itemPath, "size_bytes"), uploaded.size);
                  onChangeRoot(next);
                }}
              />
              <TextField
                label="文件名"
                fieldKey="filename"
                value={str(rec.filename)}
                disabled={disabled}
                placeholder="lease-addendum.pdf"
                onChange={(v) =>
                  onChangeRoot(setAtPath(root, childPath(itemPath, "filename"), v))
                }
              />
              <TextField
                label="添加时间"
                fieldKey="added_at"
                value={str(rec.added_at)}
                disabled={disabled}
                placeholder="YYYY-MM-DDTHH:mm:ss"
                onChange={(v) =>
                  onChangeRoot(setAtPath(root, childPath(itemPath, "added_at"), v))
                }
              />
              <p className="text-xs text-muted-foreground">
                体积：{sizeLabel(bytes)}（上传时自动写入）
              </p>
            </RowCard>
          );
        })}
      </ul>
    </ArrayShell>
  );
}
